/**
 * SettingsSection — the Console's section card.
 *
 * Replaces the old collapsed AccordionItem: content is always visible, with a
 * tinted header row (icon chip, title, description, scope badge, optional
 * right-slot). The `id` doubles as the scroll anchor used by settings search
 * and legacy deep-links; `data-settings-section` lets the orchestrator find
 * and flash-highlight it after a jump.
 */
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ScopeBadge } from './ScopeBadge';
import type { SettingScope } from '../../areas';

interface SettingsSectionProps {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  scope?: SettingScope;
  /** Right-aligned header content (status pill, action button). */
  headerRight?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function SettingsSection({
  id,
  title,
  description,
  icon: Icon,
  scope,
  headerRight,
  className,
  children,
}: SettingsSectionProps) {
  return (
    <section
      id={id}
      data-settings-section
      aria-label={typeof title === 'string' ? title : undefined}
      className={cn(
        'rounded-lg border bg-card shadow-sm overflow-hidden scroll-mt-24',
        'transition-shadow duration-200',
        className,
      )}
    >
      <header className="flex items-start gap-3 border-b bg-muted/40 px-4 py-3">
        {Icon ? (
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold leading-6">{title}</h3>
            {scope ? <ScopeBadge scope={scope} /> : null}
          </div>
          {description ? (
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          ) : null}
        </div>
        {headerRight ? <div className="shrink-0 self-center">{headerRight}</div> : null}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}
